import { SolPathPopped } from './sol-path-popped';

/**
 * Structured object that represents a data path to a specific field, object or array.
 *
 * Paths are always based on property names (instance names), never on type ids/class names.
 *
 * For example:
 * - "addresses[2].street"
 * - "addresses[2]"
 * - "addresses"
 */
export class SolPath {
  public static root: string = '';

  /**
   * Returns true if the path has an index, e.g.:
   * - "firstName[2]" => true
   * - "firstName" => false
   */
  public static hasIndex(path: string): boolean {
    return this.index(path) !== -1;
  }

  /**
   * Retrieves the last index of the path, e.g.:
   * - "firstName[2]" => 2
   * - "firstName" => -1
   */
  public static index(path: string): number {
    const indexString = this.toIndexString(path);
    return indexString !== undefined ? parseInt(indexString) : -1;
  }

  /**
   * Navigates to the given path
   */
  public static go(path: string, subPath: string): string {
    const isPathRoot = SolPath.isRoot(path);
    const isSubPathRoot = SolPath.isRoot(subPath);
    if (isPathRoot && isSubPathRoot) {
      return SolPath.root;
    } else if (isPathRoot) {
      return subPath;
    } else if (isSubPathRoot) {
      return path;
    } else {
      return `${path}.${subPath}`;
    }
  }

  public static at(path: string, index: number): string {
    return `${path}[${index}]`;
  }

  /**
   * Goes up one level in the path, e.g.:
   * - "addresses[2].street" => "addresses[2]"
   * - "addresses[2]" => "addresses"
   * - "addresses" => "" (root)
   */
  public static up(path: string): string {
    // Index
    if (path.endsWith(']')) {
      const lastBracketIndex = path.lastIndexOf('[');
      if (lastBracketIndex === -1) {
        throw new Error(`Invalid path: ${path}`);
      }
      return path.substring(0, lastBracketIndex);
    }

    // Dot
    const lastDotIndex = path.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return SolPath.root;
    }
    return path.substring(0, lastDotIndex);
  }

  /**
   * Pops the first property segment of the path and returns the rest, e.g.:
   * - "addresses[2].street" => { name: "addresses", index: 2, remainder: "street" }
   * - "addresses[2]" => { name: "addresses", index: 2, remainder: "" }
   * - "addresses" => { name: "addresses", index: -1, remainder: "" }
   * - "person.firstName" => { name: "person", index: -1, remainder: "firstName" }
   */
  public static pop(path: string): SolPathPopped {
    const bracketIndex = path.indexOf('[');
    const dotIndex = path.indexOf('.');
    let name: string;
    let index: number;
    let remainder: string;
    if (bracketIndex !== -1 && (dotIndex === -1 || bracketIndex < dotIndex)) {
      name = path.substring(0, bracketIndex);
      index = parseInt(path.substring(bracketIndex + 1, path.length - 1));

      const closingBracketIndex = path.indexOf(']');
      let remainderString = path.substring(closingBracketIndex + 1);
      remainderString = remainderString.startsWith('.') ? remainderString.substring(1) : remainderString;
      remainder = remainderString;
    } else if (dotIndex !== -1) {
      name = path.substring(0, dotIndex);
      index = -1;
      remainder = path.substring(dotIndex + 1);
    } else {
      name = path;
      index = -1;
      remainder = SolPath.root;
    }

    return { name, index, remainder };
  }

  /**
   * Returns true if the candidate path starts with the given prefix as a proper path segment.
   * A match requires that the character after the prefix is '.', '[', or end-of-string.
   *
   * For example, with prefix "aggregate":
   * - "aggregate"        => true  (exact match)
   * - "aggregate.foo"    => true  (nested property)
   * - "aggregate[0]"     => true  (indexed)
   * - "aggregateField"   => false (different property)
   */
  public static isPrefixOf(prefix: string, candidate: string): boolean {
    if (!candidate.startsWith(prefix)) return false;
    if (candidate.length === prefix.length) return true;
    const next = candidate[prefix.length];
    return next === '.' || next === '[';
  }

  /**
   * Check if the path is the root path "" (points to the object itself).
   */
  public static isRoot(path: string): boolean {
    return path === '';
  }

  /**
   * Returns the index string of the path when available, e.g.:
   * - "firstName[2]" => "2"
   */
  public static toIndexString(path: string): string | undefined {
    const match = path.match(/\[(\d+)\]$/);
    return match ? match[1] : undefined;
  }
}
